import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function Affiliates() {
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ name: "", email: "", platform_links: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "Affiliates | Card Lobster";
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      return toast.error("Name and email are required");
    }
    setBusy(true);
    try {
      await api.post("/affiliates/apply", form);
      setSent(true);
      toast.success("Application submitted — we'll be in touch");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16" data-testid="affiliates-page">
      <div className="max-w-3xl">
        <div className="text-label text-[var(--cl-primary)]">{settings.affiliates_kicker || "Partner Program"}</div>
        <h1 className="font-display text-5xl sm:text-7xl mt-2">
          {settings.affiliates_title || "Join the Card Lobster Affiliate Program"}
        </h1>
        <p className="text-zinc-300 text-lg mt-6 leading-relaxed">
          {settings.affiliates_body ||
            "Want to turn your passion for the hobby into profit? Partner with Card Lobster and earn commissions by promoting our premium sports card inventory and custom mystery packs."}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mt-14">
        <section>
          <h2 className="font-display text-3xl">What You'll Be Promoting</h2>
          <ul className="mt-6 space-y-4 text-zinc-300 text-sm">
            <Bullet title="Exclusive Mystery Packs">
              Custom-curated, heat-sealed foil bags — Gold Label, Black Label, Silver Label, and White Label series.
            </Bullet>
            <Bullet title="Premium Inventory">
              High-end singles, graded slabs, and highly sought-after case hits across MLB, NFL, and NBA.
            </Bullet>
            <Bullet title="Live Streams">
              Drive traffic to our live Whatnot auctions <span className="text-[var(--cl-orange)]">@Lobster5</span>.
            </Bullet>
          </ul>

          <h2 className="font-display text-3xl mt-12">How It Works</h2>
          <ol className="mt-6 space-y-4 text-zinc-300 text-sm">
            <Step n="1" title="Apply">
              Fill out the partnership inquiry form to tell us about your platform and audience.
            </Step>
            <Step n="2" title="Share">
              Get your custom tracking link and share it on your socials, YouTube, or website.
            </Step>
            <Step n="3" title="Earn">
              Receive a percentage of every qualifying sale made through your link.
            </Step>
          </ol>
        </section>

        <section id="apply">
          <div className="border border-white/10 rounded-lg bg-[var(--cl-surface)] p-8 sticky top-24">
            {sent ? (
              <div className="text-center py-8" data-testid="affiliates-success">
                <CheckCircle2 size={48} className="mx-auto text-[var(--cl-orange)]" />
                <h3 className="font-display text-3xl mt-4">Application received</h3>
                <p className="text-zinc-400 text-sm mt-3">
                  Thanks for applying. We'll review your application and reach out within 5 business days.
                </p>
              </div>
            ) : (
              <>
                <h3 className="font-display text-3xl">Ready to team up?</h3>
                <p className="text-zinc-400 text-sm mt-2">Fill out the form and we'll be in touch.</p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                  {/* Honeypot: hidden from humans, bots fill it and get silently dropped */}
                  <input type="text" name="website" tabIndex={-1} autoComplete="off"
                         data-testid="aff-honeypot"
                         value={form.website} onChange={(e) => upd("website", e.target.value)}
                         aria-hidden="true"
                         style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
                  <Field label="Name *" value={form.name} onChange={(v) => upd("name", v)} testid="aff-name" />
                  <Field label="Email *" type="email" value={form.email} onChange={(v) => upd("email", v)} testid="aff-email" />
                  <Field label="Social Media / Platform Links" value={form.platform_links}
                         onChange={(v) => upd("platform_links", v)} testid="aff-platforms"
                         placeholder="youtube.com/@you, tiktok.com/@you" />
                  <div>
                    <label className="text-label text-zinc-500">Tell us about your audience</label>
                    <textarea rows={5} value={form.message} onChange={(e) => upd("message", e.target.value)}
                              data-testid="aff-message"
                              className="w-full mt-2 bg-black border border-white/10 rounded px-4 py-3 text-sm focus:outline-none focus:border-[var(--cl-primary)]" />
                  </div>
                  <button disabled={busy} className="btn-primary w-full" data-testid="aff-submit">
                    {busy ? "Submitting…" : "Apply Now"}
                  </button>
                </form>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function Bullet({ title, children }) {
  return (
    <li className="flex gap-3">
      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[var(--cl-orange)] flex-shrink-0" />
      <div><span className="text-white font-medium">{title}:</span> {children}</div>
    </li>
  );
}

function Step({ n, title, children }) {
  return (
    <li className="flex gap-4">
      <span className="font-display text-3xl text-[var(--cl-orange)] leading-none flex-shrink-0 w-8">{n}</span>
      <div><span className="text-white font-medium">{title}.</span> {children}</div>
    </li>
  );
}

function Field({ label, type = "text", value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="text-label text-zinc-500">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             data-testid={testid}
             className="w-full mt-2 bg-black border border-white/10 rounded px-4 py-3 text-sm focus:outline-none focus:border-[var(--cl-primary)]" />
    </div>
  );
}
